import { match } from 'ts-pattern'
import type { Interval } from 'src/services/apollo/generated/graphql'
import { type UserSubscriptionDetailFragment } from 'src/services/apollo/generated/graphql'
import dayjs from 'src/utils/dayjs'
import { PaymentProvider, PaymentProviders } from './expireStateUtils'

/**
 * - `none` - No subscription info
 * - `expired` - Subscription has expired
 *   -`previous` field contains the last subscription tier and end date.
 * - `active` - Subscription is active
 *   - `current` contains the current subscription tier, end date, payment provider, and auto-renewal status.
 *   - the payment provider is determined by the subscription provider field, provider like apple2 or google2
 *     will be mapped to apple or google without the version suffix.
 *   - autoRenewable status can be:
 *     - `enabled` - Subscription is set to auto-renew.
 *     - `unspecified` - Subscription is NOT set to auto-renew, and is NOT managed by a mobile app store.
 *     - `mobile-app-store` - Subscription is managed by a mobile app store (e.g., Apple App Store, Google Play Store).
 *   - `next` contains the next subscription tier and period if available.
 */
export type SubscriptionState =
  | {
      state: 'active'
      current: {
        tier: number
        paymentProvider: PaymentProvider
        endAt: dayjs.Dayjs
        autoRenewable: 'enabled' | 'unspecified' | 'mobile-app-store'
      }
      next: { tier: number; period: Interval } | undefined
    }
  | { state: 'expired'; previous: { tier: number; endAt: dayjs.Dayjs } }
  | { state: 'none' }

export function getMembershipTierConfig(tier?: number) {
  return match(tier)
    .returnType<{
      label: string
      color: string
      /** More saturated colors for dark mode */
      darkColor: string
    }>()
    .with(1, () => ({
      label: 'membership:membership.tier.1.label',
      color: '#0000',
      darkColor: '#0000',
    }))
    .with(2, () => ({
      label: 'membership:membership.tier.2.label',
      color: '#e9d5ff',
      darkColor: '#581c87a0',
    }))
    .with(3, () => ({
      label: 'membership:membership.tier.3.label',
      color: '#e2e8f0',
      darkColor: '#374151',
    }))
    .otherwise(() => ({
      label: 'Unknown',
      color: '#FFF',
      darkColor: '#000',
    }))
}

const getSubscriptionAutoRenewState = (
  subscription: UserSubscriptionDetailFragment,
) => {
  const cancelAtEnd = subscription.cancelAtPeriodEnd
  const provider = subscription.provider

  if (provider?.startsWith('apple')) {
    return 'mobile-app-store' as const
  }
  if (provider?.startsWith('google')) {
    return 'mobile-app-store' as const
  }

  if (cancelAtEnd === false) {
    return 'enabled' as const
  }

  return 'unspecified' as const
}

const getSubscriptionPaymentProvider = (
  subscription: UserSubscriptionDetailFragment,
): PaymentProvider => {
  const provider = subscription.provider

  if (!provider) return PaymentProvider.Other

  const match = PaymentProviders.find(it => provider.startsWith(it.toString()))
  if (match) return match

  return PaymentProvider.Other
}

/**
 * Get the subscription state based on the user subscription detail.
 * @param subscription The detailed user subscription info
 * @returns The subscription state info @see SubscriptionState
 */
export function getSubscriptionState(
  subscription: UserSubscriptionDetailFragment,
): SubscriptionState {
  if (subscription.status === 'inactive') {
    return {
      state: 'expired',
      previous: {
        tier: subscription.tier ?? 0,
        endAt: dayjs(subscription.endAt),
      },
    }
  }

  if (!subscription.tier || subscription.status !== 'active') {
    return {
      state: 'none',
    }
  }

  const currentTier = subscription.tier
  return {
    state: 'active',
    current: {
      tier: currentTier,
      endAt: dayjs(subscription.endAt),
      paymentProvider: getSubscriptionPaymentProvider(subscription),
      autoRenewable: getSubscriptionAutoRenewState(subscription),
    },
    next: undefined,
  }
}
